// Microbe RTS — JS engine detection for the same-engine PvP gate.
//
// Determinism is only proven WITHIN one engine, so the relay refuses mixed-engine matches: it
// records the first joiner's engine and rejects a second joiner reporting a different one. Each
// client tags its /join request with detectEngine(). The three buckets:
//   'jsc'   — Apple JavaScriptCore (Safari / iOS WebKit) — the golden path.
//   'v8'    — Chrome/Chromium/Edge/Node (Blink + V8).
//   'other' — anything we can't confidently place (Firefox/SpiderMonkey, unknown embeds).
//
// Pure, no side effects. Runs in a browser (userAgent + feature sniff) or under Node (process.versions).

import type { Engine } from './protocol.js';

export function detectEngine(): Engine {
  const g = globalThis as {
    process?: { versions?: { v8?: string } };
    navigator?: { userAgent?: string };
  };

  // Node (and other V8 embeds) expose process.versions.v8 — unambiguous.
  if (g.process?.versions?.v8) return 'v8';

  const ua = g.navigator?.userAgent ?? '';
  const isWebKit = /AppleWebKit\//.test(ua);
  // Blink-based browsers also carry "AppleWebKit" in their UA for legacy reasons, so we must
  // exclude the known Blink brand tokens to isolate true Safari/JSC.
  const isBlink = /(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|SamsungBrowser)\//.test(ua);

  if (isWebKit && !isBlink) return 'jsc'; // Safari / iOS WebKit → JavaScriptCore
  if (isBlink) return 'v8';

  // Feature sniff fallback for UA-stripped WebKit views: Safari exposes non-standard prefixed globals.
  const gw = globalThis as Record<string, unknown>;
  if ('GestureEvent' in gw || 'webkitConvertPointFromNodeToPage' in gw) return 'jsc';

  return 'other';
}
